"use client";
import {
  Button,
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeftFilled } from "@fluentui/react-icons";
import { useQueryClient } from "@skanska/vsa-client-auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ActivityForm, {
  ActivityFormOpenAccordionSections,
} from "../components/activity/ActivityForm";
import { CacheKeys } from "../constants/CacheKeys";
import {
  useGetActivityDraft,
  useGetActivityFields,
  useUpdateActivityDraft,
} from "../hooks/useActivities";
import {
  Activity,
  ActivityFormData,
  UpdateActivityRequest,
} from "../models/Activity";
import { ClientError } from "../models/ClientError";
import {
  getProcessFlowSettingsForRequest,
  getProcessFlowHeadSettingsForRequest,
  shouldInheritProcessFlowSettingsForRequest,
} from "../services/ActivityService";
import { validateFormFieldsForRequest } from "../services/FieldService";
import { replaceImageSrcWithImageIds } from "../services/ImageService";

const useClasses = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

function EditActivity() {
  const classes = useClasses();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const autoOpenPublishDialog = Boolean(
    (location.state as { openPublishOnLoad?: boolean } | null)
      ?.openPublishOnLoad
  );

  const hasClearedPublishIntent = useRef(false);
  const [openAccordionSections, setOpenAccordionSections] =
    useState<ActivityFormOpenAccordionSections>({
      processFlow: false,
      tags: false,
      tools: false,
    });
  const [savedActivity, setSavedActivity] = useState<Activity>();
  const [saveVersion, setSaveVersion] = useState(0);

  useEffect(() => {
    if (autoOpenPublishDialog && !hasClearedPublishIntent.current) {
      hasClearedPublishIntent.current = true;
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [autoOpenPublishDialog, location.pathname, navigate]);

  if (!id) {
    const missingIdError: ClientError = {
      message: "Activity id is missing",
      name: "Activity id is missing",
    };

    throw missingIdError;
  }

  const idString = id as string;

  const {
    data: activity,
    isLoading,
    isRefetching,
  } = useGetActivityDraft(idString);
  const { mutateAsync: updateActivity, isPending } =
    useUpdateActivityDraft(idString);
  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetActivityFields();
  const existingActivity = savedActivity ?? activity;

  async function onFormSubmit(formData: ActivityFormData) {
    if (!viewDefinition) {
      return;
    }
    const requestFields = validateFormFieldsForRequest(
      formData.fieldsWithValues,
      viewDefinition.fields
    );

    // Convert base64 image src to data-image-id
    const serializedHtml = formData.richText
      ? replaceImageSrcWithImageIds(formData.richText)
      : formData.richText;

    const updateRequest: UpdateActivityRequest = {
      versionId: idString,
      title: formData.title,
      serializedHtml: serializedHtml,
      fieldsWithValues: requestFields,
      toolIds: formData.toolIds,
      mirroredActivityId: formData.mirroredActivityId,
      imageIds: formData.imageIds,
      imageVersionIds: formData.imageVersionIds,
      toolLinkIds: formData.toolLinkIds,
      processFlowSettings: getProcessFlowSettingsForRequest(formData),
      processFlowHeadSettings: getProcessFlowHeadSettingsForRequest(formData),
      inheritProcessFlowSettings:
        shouldInheritProcessFlowSettingsForRequest(formData),
    };

    const updatedActivity = await updateActivity(updateRequest);
    setSavedActivity(updatedActivity);
    setSaveVersion((version) => version + 1);

    await queryClient.refetchQueries({
      queryKey: [CacheKeys.Activity.ActivityDraft, { versionId: idString }],
      exact: true,
    });

    queryClient.setQueryData(
      [CacheKeys.Activity.ActivityDraft, { versionId: idString }],
      updatedActivity
    );
  }

  return (
    <div className={classes.root}>
      <h1>{t("editActivity")}</h1>
      {!isLoading && !viewDefinitionLoading ? (
        <ActivityForm
          key={`${existingActivity?.versionId ?? idString}-${saveVersion}`}
          isLoading={isPending}
          isRefetching={isRefetching}
          existingActivity={existingActivity}
          onSubmit={onFormSubmit}
          activityFields={viewDefinition?.fields}
          autoOpenPublishDialog={autoOpenPublishDialog}
          initialOpenAccordionSections={openAccordionSections}
          onOpenAccordionSectionsChange={setOpenAccordionSections}
        />
      ) : (
        <Spinner />
      )}
      <div className={classes.buttons}>
        <Button
          icon={<ArrowLeftFilled />}
          size="small"
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Button>
      </div>
    </div>
  );
}

export default EditActivity;
